import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Mail, KeyRound, Check, AlertCircle } from "lucide-react";
import { Button } from "@v2/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@v2/components/ui/card";
import { Input } from "@v2/components/ui/input";
import { Label } from "@v2/components/ui/label";
import { supabaseV2 } from "../../lib/supabase";
import { LangSwitcher } from "../../components/LangSwitcher";

type Mode = "view" | "email" | "password";

export function AccountCard({ currentEmail }: { currentEmail: string }) {
  const { t } = useTranslation("account");
  const [mode, setMode] = useState<Mode>("view");
  const [newEmail, setNewEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [info, setInfo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setMode("view");
    setNewEmail("");
    setNewPassword("");
    setInfo(null);
    setError(null);
  };

  const submitEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newEmail.trim()) return;
    setBusy(true);
    setInfo(null);
    setError(null);
    const { error: updErr } = await supabaseV2.auth.updateUser({ email: newEmail.trim() });
    setBusy(false);
    if (updErr) {
      setError(updErr.message);
      return;
    }
    setInfo(t("emailConfirmSent", { email: newEmail.trim(), current: currentEmail }));
    setNewEmail("");
    setMode("view");
  };

  const submitPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword.length < 8) {
      setError(t("passwordTooShort"));
      return;
    }
    setBusy(true);
    setInfo(null);
    setError(null);
    const { error: updErr } = await supabaseV2.auth.updateUser({ password: newPassword });
    setBusy(false);
    if (updErr) {
      setError(updErr.message);
      return;
    }
    setInfo(t("passwordUpdated"));
    setNewPassword("");
    setMode("view");
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg">{t("title")}</CardTitle>
        <CardDescription>{t("description")}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center justify-between gap-3 flex-wrap rounded-2xl bg-raised px-3 py-2">
          <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">
            {t("languageLabel")}
          </p>
          <LangSwitcher compact />
        </div>
        <div className="flex items-center justify-between gap-3 flex-wrap rounded-2xl bg-raised px-3 py-2">
          <div className="min-w-0 flex items-center gap-2">
            <Mail className="h-4 w-4 text-muted-foreground" />
            <div className="min-w-0">
              <p className="text-xs text-muted-foreground">{t("emailLabel")}</p>
              <p className="text-sm font-medium truncate">{currentEmail}</p>
            </div>
          </div>
          {mode !== "email" && (
            <Button size="sm" variant="outline" onClick={() => { reset(); setMode("email"); }}>
              {t("changeEmail")}
            </Button>
          )}
        </div>

        {mode === "email" && (
          <form onSubmit={submitEmail} className="rounded-2xl bg-raised p-3 space-y-2">
            <Label htmlFor="acc-new-email" className="text-xs">{t("newEmail")}</Label>
            <Input
              id="acc-new-email"
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder={t("newEmailPh")}
              required
              autoFocus
            />
            <p className="text-[11px] text-muted-foreground">
              {t("newEmailHint")}
            </p>
            <div className="flex gap-2">
              <Button type="submit" size="sm" disabled={busy || !newEmail.trim()}>
                {busy ? t("sending") : t("sendConfirm")}
              </Button>
              <Button type="button" size="sm" variant="ghost" onClick={reset}>
                {t("cancel")}
              </Button>
            </div>
          </form>
        )}

        <div className="flex items-center justify-between gap-3 flex-wrap rounded-2xl bg-raised px-3 py-2">
          <div className="min-w-0 flex items-center gap-2">
            <KeyRound className="h-4 w-4 text-muted-foreground" />
            <div className="min-w-0">
              <p className="text-xs text-muted-foreground">{t("passwordLabel")}</p>
              <p className="text-sm font-medium tracking-widest">••••••••</p>
            </div>
          </div>
          {mode !== "password" && (
            <Button size="sm" variant="outline" onClick={() => { reset(); setMode("password"); }}>
              {t("changePassword")}
            </Button>
          )}
        </div>

        {mode === "password" && (
          <form onSubmit={submitPassword} className="rounded-2xl bg-raised p-3 space-y-2">
            <Label htmlFor="acc-new-pwd" className="text-xs">{t("newPassword")}</Label>
            <Input
              id="acc-new-pwd"
              name="new-password"
              type="password"
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              minLength={8}
              required
              autoFocus
            />
            <p className="text-[11px] text-muted-foreground">
              {t("newPasswordHint")}
            </p>
            <div className="flex gap-2">
              <Button type="submit" size="sm" disabled={busy || newPassword.length < 8}>
                {busy ? t("saving") : t("updatePassword")}
              </Button>
              <Button type="button" size="sm" variant="ghost" onClick={reset}>
                {t("cancel")}
              </Button>
            </div>
          </form>
        )}

        {info && (
          <div className="flex items-start gap-2 text-xs text-emerald-700 bg-emerald-50 px-3 py-2 rounded-md">
            <Check className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <span>{info}</span>
          </div>
        )}
        {error && (
          <div className="flex items-start gap-2 text-xs text-rose-600 bg-rose-50 px-3 py-2 rounded-md">
            <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
